// Items in the style of Might and Magic III: a base kind ("Long Sword"), a
// material that scales with dungeon depth ("Silver"), and sometimes an
// enchantment ("of Power"). Deeper levels give better materials and a better
// chance of an enchantment.

export const SLOTS = ['weapon', 'missile', 'armor', 'shield', 'helm', 'cloak', 'boots', 'amulet', 'ring'];

// Potions live in the pack and are drunk, never equipped
export const CONSUMABLES = [
    { kind: 'Healing Potion', restores: 'hp', amount: 15, value: 60 },
    { kind: 'Greater Healing Potion', restores: 'hp', amount: 35, value: 150, depth: 5 },
    { kind: 'Mana Potion', restores: 'sp', amount: 8, value: 70 },
    { kind: 'Greater Mana Potion', restores: 'sp', amount: 18, value: 160, depth: 5 },
];

export const SLOT_NAMES = {
    weapon: 'Weapon',
    missile: 'Missile',
    armor: 'Armour',
    shield: 'Shield',
    helm: 'Helm',
    cloak: 'Cloak',
    boots: 'Boots',
    amulet: 'Amulet',
    ring: 'Ring',
};

// Base kinds per slot. Weapons carry dice, armour carries an armour class.
export const KINDS = {
    weapon: [
        { kind: 'Dagger', dice: [1, 4], value: 20 },
        { kind: 'Staff', dice: [1, 6], value: 25 },
        { kind: 'Short Sword', dice: [1, 6], value: 40 },
        { kind: 'Mace', dice: [1, 6], value: 40 },
        { kind: 'Axe', dice: [1, 8], value: 70 },
        { kind: 'Spear', dice: [1, 8], value: 60 },
        { kind: 'Long Sword', dice: [1, 10], value: 110 },
        { kind: 'Battle Axe', dice: [1, 12], value: 150 },
    ],
    missile: [
        { kind: 'Sling', dice: [1, 4], projectile: 'rock', value: 20 },
        { kind: 'Short Bow', dice: [1, 6], projectile: 'arrow', value: 50 },
        { kind: 'Crossbow', dice: [1, 6], projectile: 'bolt', value: 70 },
        { kind: 'Long Bow', dice: [1, 8], projectile: 'arrow', value: 100 },
    ],
    armor: [
        { kind: 'Robes', ac: 1, value: 20 },
        { kind: 'Leather Armour', ac: 3, value: 50 },
        { kind: 'Scale Mail', ac: 5, value: 100 },
        { kind: 'Chain Mail', ac: 6, value: 160 },
        { kind: 'Plate Mail', ac: 8, value: 260 },
    ],
    shield: [{ kind: 'Buckler', ac: 1, value: 30 }, { kind: 'Shield', ac: 2, value: 70 }, { kind: 'Tower Shield', ac: 3, value: 130 }],
    helm: [{ kind: 'Cap', ac: 1, value: 25 }, { kind: 'Helm', ac: 2, value: 60 }],
    cloak: [{ kind: 'Cloak', ac: 1, value: 40 }],
    boots: [{ kind: 'Boots', ac: 1, value: 35 }],
    amulet: [{ kind: 'Amulet', ac: 0, value: 80 }],
    ring: [{ kind: 'Ring', ac: 0, value: 80 }],
};

// Material tiers: bonus applies to damage or armour class, and multiplies worth
export const MATERIALS = [
    { name: 'Wooden', bonus: 0, value: 0.5, depth: 0 },
    { name: 'Leather', bonus: 0, value: 0.7, depth: 0 },
    { name: 'Bronze', bonus: 1, value: 1, depth: 1 },
    { name: 'Iron', bonus: 2, value: 1.5, depth: 2 },
    { name: 'Steel', bonus: 3, value: 2.2, depth: 4 },
    { name: 'Silver', bonus: 4, value: 3.2, depth: 6 },
    { name: 'Gold', bonus: 5, value: 4.5, depth: 8 },
    { name: 'Platinum', bonus: 6, value: 6, depth: 11 },
    { name: 'Obsidian', bonus: 8, value: 8, depth: 14 },
    { name: 'Diamond', bonus: 10, value: 12, depth: 17 },
];

// Enchantments raise an attribute, armour class or damage
export const ENCHANTMENTS = [
    { name: 'of Power', stat: 'might' },
    { name: 'of Precision', stat: 'accuracy' },
    { name: 'of Speed', stat: 'speed' },
    { name: 'of Wisdom', stat: 'intellect' },
    { name: 'of Faith', stat: 'personality' },
    { name: 'of Vigour', stat: 'endurance' },
    { name: 'of Fortune', stat: 'luck' },
    { name: 'of Protection', ac: true },
    { name: 'of Slaying', damage: true },
];

let nextItemId = 1;

function pick(list, rng) {
    return list[Math.floor(rng() * list.length)];
}

// Materials available at this depth, weighted towards the best ones on offer
function materialsFor(level) {
    const available = MATERIALS.filter((m) => m.depth <= level + 1);
    return available.slice(Math.max(0, available.length - 4));
}

export function createPotion(kind) {
    const base = CONSUMABLES.find((c) => c.kind === kind) || CONSUMABLES[0];
    return {
        id: 'i' + (nextItemId++),
        slot: 'consumable',
        kind: base.kind,
        material: null,
        enchantment: null,
        restores: base.restores,
        amount: base.amount,
        value: base.value,
    };
}

// A potion suited to the depth, or null if this level has none to offer
export function generatePotion(level, rng = Math.random) {
    const available = CONSUMABLES.filter((c) => (c.depth || 0) <= level);
    return createPotion(available[Math.floor(rng() * available.length)].kind);
}

export function createItem({ slot, kind, material, enchantment }) {
    const base = KINDS[slot].find((k) => k.kind === kind) || KINDS[slot][0];
    const item = {
        id: 'i' + (nextItemId++),
        slot,
        kind: base.kind,
        material: material ? material.name : null,
        enchantment: enchantment ? { ...enchantment } : null,
        bonus: material ? material.bonus : 0,
    };
    if (base.dice) item.dice = [...base.dice];
    if (base.projectile) item.projectile = base.projectile;
    if (base.ac !== undefined) item.ac = base.ac;

    // What the material and enchantment add on top of the base kind
    item.toHit = base.dice ? item.bonus : 0;
    item.damageBonus = base.dice ? item.bonus : 0;
    if (base.ac !== undefined) item.acBonus = item.bonus;
    if (enchantment) {
        const power = enchantment.power || 1;
        if (enchantment.ac) item.acBonus = (item.acBonus || 0) + power * 2;
        if (enchantment.damage) item.damageBonus = (item.damageBonus || 0) + power * 2;
        if (enchantment.stat) item.statBonus = { stat: enchantment.stat, amount: power * 2 };
    }
    item.value = Math.round(
        base.value * (material ? material.value : 1) * (enchantment ? 2 + (enchantment.power || 1) : 1)
    );
    return item;
}

export function itemName(item) {
    return [item.material, item.kind, item.enchantment && item.enchantment.name].filter(Boolean).join(' ');
}

// One line describing what an item does, for the character sheet
export function itemSummary(item) {
    const parts = [];
    if (item.restores) {
        parts.push(`restores ${item.amount} ${item.restores === 'hp' ? 'hit points' : 'spell points'}`);
        parts.push(`${item.value} gold`);
        return parts.join(' · ');
    }
    if (item.dice) {
        parts.push(`${item.dice[0]}d${item.dice[1]}${item.damageBonus ? `+${item.damageBonus}` : ''} damage`);
        if (item.toHit) parts.push(`+${item.toHit} to hit`);
    }
    const armour = (item.ac || 0) + (item.acBonus || 0);
    if (armour) parts.push(`+${armour} AC`);
    if (item.statBonus) parts.push(`+${item.statBonus.amount} ${item.statBonus.stat}`);
    parts.push(`${item.value} gold`);
    return parts.join(' · ');
}

// A random item suited to the given dungeon level
export function generateItem(level, rng = Math.random) {
    // A third of finds are something to drink
    if (rng() < 0.33) return generatePotion(level, rng);
    const slot = pick(SLOTS, rng);
    const kinds = KINDS[slot];
    // Deeper levels lean towards the heavier kinds
    const reach = Math.min(kinds.length, 2 + Math.floor(level / 3));
    const base = kinds[Math.floor(rng() * reach)];
    const material = pick(materialsFor(level), rng);
    const enchanted = rng() < 0.15 + level * 0.02;
    const enchantment = enchanted
        ? { ...pick(ENCHANTMENTS, rng), power: 1 + Math.floor(rng() * (1 + level / 5)) }
        : null;
    return createItem({ slot, kind: base.kind, material, enchantment });
}
